import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  IconButton,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import BusinessIcon from '@mui/icons-material/Business';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import CameraIcon from '@mui/icons-material/Camera';
import CampaignIcon from '@mui/icons-material/Campaign';

// Sample credential templates with W3C VC format for media industry
const sampleTemplates = [
  {
    id: '1',
    name: 'Certified Publisher',
    description: 'Template for verified publishing organizations',
    credentialTypeId: '1', // CertifiedPublisherCredential
    template: {
      '@context': [
        'https://www.w3.org/2018/credentials/v1',
        'https://www.noosphere.tech/credentials/v1'
      ],
      'type': ['VerifiableCredential', 'CertifiedPublisherCredential'],
      'issuer': '{{issuerDid}}',
      'issuanceDate': '{{issuanceDate}}',
      'expirationDate': '{{expirationDate}}',
      'credentialSubject': {
        'id': '{{subjectDid}}',
        'publisherName': '{{publisherName}}',
        'jurisdictions': '{{jurisdictions}}',
        'publicationTypes': '{{publicationTypes}}',
        'establishedDate': '{{establishedDate}}',
        'registrationNumber': '{{registrationNumber}}',
        'complianceStatus': '{{complianceStatus}}',
        'verificationLevel': '{{verificationLevel}}'
      },
      'credentialStatus': {
        'id': '{{statusId}}',
        'type': 'CredentialStatusList2021'
      }
    }
  },
  {
    id: '2',
    name: 'Certified Journalist',
    description: 'Template for verified journalists',
    credentialTypeId: '2', // CertifiedJournalistCredential
    template: {
      '@context': [
        'https://www.w3.org/2018/credentials/v1',
        'https://www.noosphere.tech/credentials/v1'
      ],
      'type': ['VerifiableCredential', 'CertifiedJournalistCredential'],
      'issuer': '{{issuerDid}}',
      'issuanceDate': '{{issuanceDate}}',
      'expirationDate': '{{expirationDate}}',
      'credentialSubject': {
        'id': '{{subjectDid}}',
        'name': '{{name}}',
        'specialization': '{{specialization}}',
        'publications': '{{publications}}',
        'yearsExperience': '{{yearsExperience}}',
        'degrees': '{{degrees}}',
        'associations': '{{associations}}',
        'awards': '{{awards}}'
      },
      'credentialStatus': {
        'id': '{{statusId}}',
        'type': 'CredentialStatusList2021'
      }
    }
  },
  {
    id: '3',
    name: 'Certified Photojournalist',
    description: 'Template for verified photojournalists',
    credentialTypeId: '3', // CertifiedPhotojournalistCredential
    template: {
      '@context': [
        'https://www.w3.org/2018/credentials/v1',
        'https://www.noosphere.tech/credentials/v1'
      ],
      'type': ['VerifiableCredential', 'CertifiedPhotojournalistCredential'],
      'issuer': '{{issuerDid}}',
      'issuanceDate': '{{issuanceDate}}',
      'expirationDate': '{{expirationDate}}',
      'credentialSubject': {
        'id': '{{subjectDid}}',
        'name': '{{name}}',
        'specialization': '{{specialization}}',
        'publications': '{{publications}}',
        'equipment': '{{equipment}}',
        'portfolio': '{{portfolio}}',
        'associations': '{{associations}}',
        'awards': '{{awards}}'
      },
      'credentialStatus': {
        'id': '{{statusId}}',
        'type': 'CredentialStatusList2021'
      }
    }
  },
  {
    id: '4',
    name: 'Certified Advertiser',
    description: 'Template for verified advertising organizations',
    credentialTypeId: '4', // CertifiedAdvertiserCredential
    template: {
      '@context': [
        'https://www.w3.org/2018/credentials/v1',
        'https://www.noosphere.tech/credentials/v1'
      ],
      'type': ['VerifiableCredential', 'CertifiedAdvertiserCredential'],
      'issuer': '{{issuerDid}}',
      'issuanceDate': '{{issuanceDate}}',
      'expirationDate': '{{expirationDate}}',
      'credentialSubject': {
        'id': '{{subjectDid}}',
        'companyName': '{{companyName}}',
        'adTypes': '{{adTypes}}',
        'industries': '{{industries}}',
        'establishedDate': '{{establishedDate}}',
        'verificationStatus': '{{verificationStatus}}',
        'complianceFrameworks': '{{complianceFrameworks}}',
        'certifications': '{{certifications}}'
      },
      'credentialStatus': {
        'id': '{{statusId}}',
        'type': 'CredentialStatusList2021'
      }
    }
  }
];

// Get icon for credential type
const getCredentialTypeIcon = (typeId) => {
  switch(typeId) {
    case '1': return <BusinessIcon color="primary" />;
    case '2': return <NewspaperIcon color="primary" />;
    case '3': return <CameraIcon color="primary" />;
    case '4': return <CampaignIcon color="primary" />;
    default: return <VerifiedUserIcon color="primary" />;
  }
};

// Format credential type name
const formatCredentialTypeName = (typeName) => {
  if (typeName === 'CertifiedPublisherCredential') return 'Certified Publisher';
  if (typeName === 'CertifiedJournalistCredential') return 'Certified Journalist';
  if (typeName === 'CertifiedPhotojournalistCredential') return 'Certified Photojournalist';
  if (typeName === 'CertifiedAdvertiserCredential') return 'Certified Advertiser';
  
  // Remove "Credential" suffix and add spaces
  return typeName.replace('Credential', '').replace(/([A-Z])/g, ' $1').trim();
};

// Credential Templates component
const CredentialTemplates = ({ credentialTypes }) => {
  const [templates, setTemplates] = useState(sampleTemplates);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [jsonView, setJsonView] = useState({});
  const [jsonViewOpen, setJsonViewOpen] = useState(false);
  
  // Form state
  const [formState, setFormState] = useState({
    name: '',
    description: '',
    credentialTypeId: '',
    template: ''
  });
  
  // Handle creating new template
  const handleCreateTemplate = () => {
    setIsEditing(false);
    setFormState({
      name: '',
      description: '',
      credentialTypeId: '',
      template: JSON.stringify({
        '@context': [
          'https://www.w3.org/2018/credentials/v1',
          'https://www.noosphere.tech/credentials/v1'
        ],
        'type': ['VerifiableCredential'],
        'issuer': '{{issuerDid}}',
        'issuanceDate': '{{issuanceDate}}',
        'expirationDate': '{{expirationDate}}',
        'credentialSubject': {
          'id': '{{subjectDid}}',
          'name': '{{name}}'
        },
        'credentialStatus': {
          'id': '{{statusId}}',
          'type': 'CredentialStatusList2021'
        }
      }, null, 2)
    });
    setDialogOpen(true);
  };
  
  // Handle editing a template
  const handleEditTemplate = (template) => {
    setIsEditing(true);
    setCurrentTemplate(template);
    setFormState({
      name: template.name,
      description: template.description,
      credentialTypeId: template.credentialTypeId,
      template: JSON.stringify(template.template, null, 2)
    });
    setDialogOpen(true);
  };
  
  // Handle viewing a template
  const handleViewTemplate = (template) => {
    setJsonView(template.template);
    setJsonViewOpen(true);
  };
  
  // Handle deleting a template
  const handleDeleteTemplate = (id) => {
    // In a real app, show a confirmation dialog first
    setTemplates((prevTemplates) => prevTemplates.filter((template) => template.id !== id));
  };
  
  // Handle form field changes
  const handleFormChange = (field) => (event) => {
    setFormState((prev) => ({
      ...prev,
      [field]: event.target.value
    }));
  };
  
  // Handle template save
  const handleSaveTemplate = () => {
    try {
      // Parse the template JSON to validate it
      const parsedTemplate = JSON.parse(formState.template);
      
      // Create new template or update existing
      if (isEditing && currentTemplate) {
        setTemplates((prevTemplates) => 
          prevTemplates.map((template) => 
            template.id === currentTemplate.id 
              ? {
                  ...template,
                  name: formState.name,
                  description: formState.description,
                  credentialTypeId: formState.credentialTypeId,
                  template: parsedTemplate
                }
              : template
          )
        );
      } else {
        // Generate ID for new template
        const newId = (Math.max(...templates.map(t => parseInt(t.id)), 0) + 1).toString();
        
        setTemplates((prevTemplates) => [
          ...prevTemplates,
          {
            id: newId,
            name: formState.name,
            description: formState.description,
            credentialTypeId: formState.credentialTypeId,
            template: parsedTemplate
          }
        ]);
      }
      
      // Close dialog
      setDialogOpen(false);
      setCurrentTemplate(null);
    } catch (error) {
      // In a real app, show an error message about invalid JSON
      console.error('Invalid template JSON:', error);
      alert('Invalid JSON format in template. Please check and try again.');
    }
  };
  
  // Get credential type name by ID
  const getCredentialTypeName = (id) => {
    const type = credentialTypes.find((type) => type.id === id);
    return type ? formatCredentialTypeName(type.name) : 'Unknown Type';
  };
  
  // Handle JSON view close
  const handleJsonViewClose = () => {
    setJsonViewOpen(false);
  };
  
  // Handle dialog close
  const handleDialogClose = () => {
    setDialogOpen(false);
    setCurrentTemplate(null);
  };
  
  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <Typography variant="h5" fontFamily="Cousine, monospace" color="primary.main">
            Credential Templates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage templates for W3C Verifiable Credentials
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} sx={{ textAlign: 'right' }}>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={handleCreateTemplate}
            sx={{ mt: { xs: 2, sm: 0 } }}
          >
            Create New Template
          </Button>
        </Grid>
      </Grid>

      <Alert severity="info" sx={{ mb: 3 }}>
        Templates follow the W3C Verifiable Credentials Data Model 1.0 format and can be used to generate credentials with consistent structure.
      </Alert>
      
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1">About Credential Templates</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" paragraph>
            Credential templates provide a standardized structure for issuing verifiable credentials. They use placeholder values enclosed in double curly braces <code>&#123;&#123;placeholder&#125;&#125;</code> that are replaced with actual values when a credential is issued.
          </Typography>
          <Typography variant="body2" paragraph>
            All templates include the core W3C Verifiable Credentials fields:
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li><Typography variant="body2"><code>@context</code> - JSON-LD context definitions</Typography></li>
            <li><Typography variant="body2"><code>type</code> - Credential types</Typography></li>
            <li><Typography variant="body2"><code>issuer</code> - The DID of the issuer</Typography></li>
            <li><Typography variant="body2"><code>issuanceDate</code> - When the credential was issued</Typography></li>
            <li><Typography variant="body2"><code>expirationDate</code> - When the credential expires (optional)</Typography></li>
            <li><Typography variant="body2"><code>credentialSubject</code> - Claims about the subject</Typography></li>
            <li><Typography variant="body2"><code>credentialStatus</code> - Information to verify the credential's status</Typography></li>
          </Box>
        </AccordionDetails>
      </Accordion>
      
      <Grid container spacing={3} sx={{ mt: 3 }}>
        {templates.map((template) => (
          <Grid item xs={12} md={6} key={template.id}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {getCredentialTypeIcon(template.credentialTypeId)}
                  <Typography variant="h6" sx={{ ml: 1 }}>{template.name}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {template.description}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>Type:</Typography>
                  <Chip 
                    label={getCredentialTypeName(template.credentialTypeId)} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                </Box>
                
                <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
                  Template Fields:
                </Typography>
                <Box sx={{ 
                  maxHeight: '100px', 
                  overflow: 'auto', 
                  bgcolor: 'background.default',
                  p: 1,
                  borderRadius: 1,
                  fontSize: '0.8rem',
                  fontFamily: 'monospace',
                  mt: 1
                }}>
                  {Object.keys(template.template.credentialSubject).map((key) => (
                    <Box component="span" key={key} sx={{ mr: 1, display: 'inline-block' }}>
                      {typeof template.template.credentialSubject[key] === 'object'
                        ? Object.keys(template.template.credentialSubject[key]).map((subKey) => (
                            <Chip 
                              key={`${key}.${subKey}`}
                              label={`${key}.${subKey}`}
                              size="small"
                              sx={{ m: 0.5 }}
                            />
                          ))
                        : <Chip 
                            key={key}
                            label={key}
                            size="small"
                            sx={{ m: 0.5 }}
                          />
                      }
                    </Box>
                  ))}
                </Box>
              </CardContent>
              <CardActions>
                <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => handleViewTemplate(template)}>
                  View JSON
                </Button>
                <Button size="small" startIcon={<EditIcon />} onClick={() => handleEditTemplate(template)}>
                  Edit
                </Button>
                <IconButton size="small" color="error" onClick={() => handleDeleteTemplate(template.id)}>
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      {/* Create/Edit Template Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleDialogClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {isEditing ? 'Edit Credential Template' : 'Create Credential Template'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Template Name"
                fullWidth
                value={formState.name}
                onChange={handleFormChange('name')}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Credential Type</InputLabel>
                <Select
                  value={formState.credentialTypeId}
                  onChange={handleFormChange('credentialTypeId')}
                  label="Credential Type"
                >
                  {credentialTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {formatCredentialTypeName(type.name)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                value={formState.description}
                onChange={handleFormChange('description')}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                Template JSON (W3C Verifiable Credential Format)
              </Typography>
              <TextField
                multiline
                fullWidth
                rows={12}
                value={formState.template}
                onChange={handleFormChange('template')}
                margin="normal"
                variant="outlined"
                InputProps={{
                  style: { fontFamily: 'monospace', fontSize: '0.9rem' }
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Use placeholders like &#123;&#123;placeholderName&#125;&#125; for dynamic values.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSaveTemplate}
          >
            {isEditing ? 'Update Template' : 'Create Template'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* JSON View Dialog */}
      <Dialog
        open={jsonViewOpen}
        onClose={handleJsonViewClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Template JSON</DialogTitle>
        <DialogContent>
          <Paper sx={{ 
            p: 2, 
            bgcolor: 'background.default',
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            overflowX: 'auto'
          }}>
            <pre>{JSON.stringify(jsonView, null, 2)}</pre>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              navigator.clipboard.writeText(JSON.stringify(jsonView, null, 2));
            }}
            startIcon={<ContentCopyIcon />}
          >
            Copy to Clipboard
          </Button>
          <Button onClick={handleJsonViewClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CredentialTemplates;